import { useAuth } from '@components/auth/AuthContext';
import { useCurrentUser } from '@hooks/useAuth';
import { useCompanyBranding } from '@hooks/useCompany';
import { useSubmitOffer } from '@hooks/useOffers';
import { type Offer, type OfferSubmitData } from '@lib/api';
import type { Problem } from '@lib/api/types';
import { DEFAULT_CURRENCY, type CurrencyCode } from '@lib/constants';
import React, { ReactNode, createContext, useContext, useEffect, useState } from 'react';

// Define types for your state
export interface Benefit {
  id: string;
  name: string;
  type: string;
  description: string;
  url?: string;
  monetary_value?: number;
  currency?: string;
  frequency?: string;
  pdf_file?: string;
  pdf_filename?: string;
  pdf_mimetype?: string;
  derived_from_benefit_template?: string;
  order?: number;
}

export interface Bonus {
  id: number;
  type: 'recurring' | 'one_time';
  name: string;
  amount: string;
  amountType: 'flat' | 'percentage';
  currency?: string;
  frequency?: 'monthly' | 'quarterly' | 'biannual' | 'annual';
  details: string;
}

export interface EquityValues {
  currentValue: number;
  projectedValue: number;
  year1: string;
  year2: string;
  year3: string;
  year4: string;
  projectedYear1: string;
  projectedYear2: string;
  projectedYear3: string;
  projectedYear4: string;
}

export interface CompanyInfo {
  name: string;
  logo: string;
  headerImage: string;
  primaryColor: string;
  description: string;
}

interface CandidateInfo {
  firstName: string;
  lastName: string;
  position: string;
}

// Re-export API offer type
export type { Offer } from '@lib/api';

// Define types for commission and payout frequency
export type CommissionType = 'percentage' | 'tiered' | 'quota_ote' | 'draw' | 'hybrid' | '';
export type PayoutFrequency = 'monthly' | 'quarterly' | 'biannual' | 'annual' | '';

// Define a type for offer data
export interface OfferData {
  id?: string;
  candidateName: string;
  candidateEmail: string;
  role: string;
  department: string;
  location: string;
  salary: string;
  startDate: string;
  // Add other properties as needed
}

// Define a type for additional info
export interface OfferInfo {
  status?: string;
  message?: string;
  // Add other properties as needed
}

export interface OfferContextType {
  // Candidate info
  candidateName: string;
  setCandidateName: (value: string) => void;
  candidateEmail: string;
  setCandidateEmail: (value: string) => void;
  role: string;
  setRole: (value: string) => void;
  department: string;
  setDepartment: (value: string) => void;
  team: string;
  setTeam: (value: string) => void;
  location: string;
  setLocation: (value: string) => void;
  salary: string;
  setSalary: (value: string) => void;
  startDate: string;
  setStartDate: (value: string) => void;
  welcomeMessage: string;
  setWelcomeMessage: (value: string) => void;
  reportingTo: string;
  setReportingTo: (value: string) => void;
  expiresAt: string;
  setExpiresAt: (value: string) => void;
  paymentFrequency: string;
  setPaymentFrequency: (value: string) => void;

  // Benefits
  benefits: Benefit[];
  setBenefits: (benefits: Benefit[]) => void;
  newBenefitName: string;
  setNewBenefitName: (value: string) => void;
  newBenefitType: string;
  setNewBenefitType: (value: string) => void;
  newBenefitDescription: string;
  setNewBenefitDescription: (value: string) => void;
  addBenefit: () => void;
  removeBenefit: (id: string) => void;

  // Equity
  equityShares: string;
  setEquityShares: (value: string) => void;
  preferredPrice: string;
  setPreferredPrice: (value: string) => void;
  strikePrice: string;
  setStrikePrice: (value: string) => void;
  companyGrowth: number[];
  setCompanyGrowth: (value: number[]) => void;
  calculateEquityValue: () => EquityValues;

  // Commission
  hasCommission: boolean;
  setHasCommission: (value: boolean) => void;
  commissionType: CommissionType;
  setCommissionType: (value: CommissionType) => void;
  commissionTargetAmount: string;
  setCommissionTargetAmount: (value: string) => void;
  commissionBaseRate: string;
  setCommissionBaseRate: (value: string) => void;
  commissionQuotaAmount: string;
  setCommissionQuotaAmount: (value: string) => void;
  commissionPayoutFrequency: PayoutFrequency;
  setCommissionPayoutFrequency: (value: PayoutFrequency) => void;
  commissionHasAccelerators: boolean;
  setCommissionHasAccelerators: (value: boolean) => void;
  commissionShowPerformanceScenarios: boolean;
  setCommissionShowPerformanceScenarios: (value: boolean) => void;
  commissionDetails: string;
  setCommissionDetails: (value: string) => void;
  commissionTiers: Array<{
    id: number;
    threshold: string;
    rate: string;
    is_incremental: boolean;
  }>;
  setCommissionTiers: (
    value: Array<{
      id: number;
      threshold: string;
      rate: string;
      is_incremental: boolean;
    }>
  ) => void;
  addCommissionTier: (threshold: string, rate: string, is_incremental: boolean) => void;
  removeCommissionTier: (id: number) => void;
  calculateOTE: () => number;

  // Company info
  companyInfo: CompanyInfo;
  setCompanyInfo: (info: CompanyInfo) => void;

  // Submission
  isSubmitting: boolean;
  submitOfferError: Problem | null;
  submitOfferResult: Offer | null;
  submitOffer: (data?: Partial<OfferSubmitData>) => Promise<Offer>;

  // Compensation tab
  salaryCurrency: CurrencyCode;
  setSalaryCurrency: (value: CurrencyCode) => void;
  equityType: string;
  setEquityType: (value: string) => void;
  equityVesting: string;
  setEquityVesting: (value: string) => void;

  // New multiple bonuses properties
  bonuses: Bonus[];
  setBonuses: (bonuses: Bonus[]) => void;
  addBonus: (
    type: 'recurring' | 'one_time',
    amount: string,
    amountType: 'flat' | 'percentage',
    details: string,
    name?: string,
    currency?: string,
    frequency?: 'monthly' | 'quarterly' | 'biannual' | 'annual'
  ) => void;
  removeBonus: (id: number) => void;
  // Flag for each type of bonus to know if it's enabled
  hasAnnualBonus: boolean;
  setHasAnnualBonus: (value: boolean) => void;
  hasQuarterlyBonus: boolean;
  setHasQuarterlyBonus: (value: boolean) => void;
  hasPerformanceBonus: boolean;
  setHasPerformanceBonus: (value: boolean) => void;
  hasSignOnBonus: boolean;
  setHasSignOnBonus: (value: boolean) => void;
  hasRelocationBonus: boolean;
  setHasRelocationBonus: (value: boolean) => void;

  candidateInfo: CandidateInfo;
  setCandidateInfo: (info: CandidateInfo) => void;

  // Preview functionality
  previewOffer: () => OfferSubmitData;

  // New vesting fields
  yearsVesting: number;
  setYearsVesting: (value: number) => void;
  cliffPeriodMonths: number;
  setCliffPeriodMonths: (value: number) => void;

  // Equity ownership display
  showEquityOwnership: boolean;
  setShowEquityOwnership: (value: boolean) => void;

  // Equity currency conversion
  equityDisplayCurrency: string;
  setEquityDisplayCurrency: (val: string) => void;
  equityConversionRate: string;
  setEquityConversionRate: (val: string) => void;

  // Employment type fields
  employmentType: 'full_time' | 'part_time' | 'contractor';
  setEmploymentType: (value: 'full_time' | 'part_time' | 'contractor') => void;
  compensationType: 'salary' | 'hourly';
  setCompensationType: (value: 'salary' | 'hourly') => void;
  hourlyRate: string;
  setHourlyRate: (value: string) => void;
  endDate: string;
  setEndDate: (value: string) => void;

  // Flag to track if offer data was loaded (edit mode) - prevents company defaults from overriding
  offerDataLoaded: boolean;
  setOfferDataLoaded: (value: boolean) => void;

  /**
   * Whether the form carries edits that have not been saved. The legal
   * document preview is compiled from the offer's SAVED values, so this is
   * what lets the Legal Documents tab say so instead of showing a letter that
   * quietly disagrees with the form beside it.
   */
  isFormDirty: boolean;
  /** Take the "this is saved" baseline: after an edit-mode load, and after a save. */
  markOfferSaved: () => void;
}

export const OfferContext = createContext<OfferContextType>({
  candidateName: '',
  setCandidateName: () => {},
  candidateEmail: '',
  setCandidateEmail: () => {},
  role: '',
  setRole: () => {},
  department: '',
  setDepartment: () => {},
  team: '',
  setTeam: () => {},
  location: '',
  setLocation: () => {},
  salary: '',
  setSalary: () => {},
  startDate: '',
  setStartDate: () => {},
  welcomeMessage: '',
  setWelcomeMessage: () => {},
  reportingTo: '',
  setReportingTo: () => {},
  expiresAt: '',
  setExpiresAt: () => {},
  paymentFrequency: 'biweekly',
  setPaymentFrequency: () => {},
  benefits: [],
  setBenefits: () => {},
  newBenefitName: '',
  setNewBenefitName: () => {},
  newBenefitType: '',
  setNewBenefitType: () => {},
  newBenefitDescription: '',
  setNewBenefitDescription: () => {},
  addBenefit: () => {},
  removeBenefit: () => {},
  equityShares: '',
  setEquityShares: () => {},
  preferredPrice: '',
  setPreferredPrice: () => {},
  strikePrice: '',
  setStrikePrice: () => {},
  companyGrowth: [],
  setCompanyGrowth: () => {},
  calculateEquityValue: () => ({
    currentValue: 0,
    projectedValue: 0,
    year1: '0.00',
    year2: '0.00',
    year3: '0.00',
    year4: '0.00',
    projectedYear1: '0.00',
    projectedYear2: '0.00',
    projectedYear3: '0.00',
    projectedYear4: '0.00',
  }),
  companyInfo: {
    name: '',
    logo: '',
    headerImage: '',
    primaryColor: '#4f46e5',
    description: '',
  },
  setCompanyInfo: () => {},
  isSubmitting: false,
  submitOfferError: null,
  submitOfferResult: null,
  submitOffer: async (_data?: Partial<OfferSubmitData>) =>
    Promise.reject(new Error('submitOffer not initialized')),
  salaryCurrency: 'USD',
  setSalaryCurrency: () => {},
  equityType: '',
  setEquityType: () => {},
  equityVesting: '',
  setEquityVesting: () => {},
  bonuses: [],
  setBonuses: () => {},
  addBonus: () => {},
  removeBonus: () => {},
  hasAnnualBonus: false,
  setHasAnnualBonus: () => {},
  hasQuarterlyBonus: false,
  setHasQuarterlyBonus: () => {},
  hasPerformanceBonus: false,
  setHasPerformanceBonus: () => {},
  hasSignOnBonus: false,
  setHasSignOnBonus: () => {},
  hasRelocationBonus: false,
  setHasRelocationBonus: () => {},
  candidateInfo: {
    firstName: '',
    lastName: '',
    position: '',
  },
  setCandidateInfo: () => {},
  previewOffer: () => ({
    candidate: { first_name: '', last_name: '', email: '', phone_number: '' },
    role: '',
    department: '',
    location: '',
    start_date: '',
    expires_at: '',
    base_salary: 0,
    currency: 'USD',
    equity: null,
    benefits: [],
  }),
  yearsVesting: 0,
  setYearsVesting: () => {},
  cliffPeriodMonths: 0,
  setCliffPeriodMonths: () => {},
  showEquityOwnership: false,
  setShowEquityOwnership: () => {},
  equityDisplayCurrency: 'USD',
  setEquityDisplayCurrency: () => {},
  equityConversionRate: '',
  setEquityConversionRate: () => {},
  employmentType: 'full_time',
  setEmploymentType: () => {},
  compensationType: 'salary',
  setCompensationType: () => {},
  hourlyRate: '',
  setHourlyRate: () => {},
  endDate: '',
  setEndDate: () => {},
  hasCommission: false,
  setHasCommission: () => {},
  commissionType: '',
  setCommissionType: () => {},
  commissionTargetAmount: '',
  setCommissionTargetAmount: () => {},
  commissionBaseRate: '',
  setCommissionBaseRate: () => {},
  commissionQuotaAmount: '',
  setCommissionQuotaAmount: () => {},
  commissionPayoutFrequency: '',
  setCommissionPayoutFrequency: () => {},
  commissionHasAccelerators: false,
  setCommissionHasAccelerators: () => {},
  commissionShowPerformanceScenarios: true,
  setCommissionShowPerformanceScenarios: () => {},
  commissionDetails: '',
  setCommissionDetails: () => {},
  commissionTiers: [],
  setCommissionTiers: () => {},
  addCommissionTier: () => {},
  removeCommissionTier: () => {},
  calculateOTE: () => 0,
  offerDataLoaded: false,
  setOfferDataLoaded: () => {},
  isFormDirty: false,
  markOfferSaved: () => {},
});

export const OfferProvider: React.FC<{ children: ReactNode; editingExistingOffer?: boolean }> = ({
  children,
  editingExistingOffer = false,
}) => {
  const { user: _user2 } = useAuth();
  const { companyBranding } = useCompanyBranding();
  const { data: _user } = useCurrentUser();

  // Candidate info
  const [candidateName, setCandidateName] = useState<string>('');
  const [candidateEmail, setCandidateEmail] = useState<string>('');
  const [role, setRole] = useState<string>('');
  const [department, setDepartment] = useState<string>('');
  const [team, setTeam] = useState<string>('');
  const [location, setLocation] = useState<string>('');
  const [salary, setSalary] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [welcomeMessage, setWelcomeMessage] = useState<string>('');
  const [reportingTo, setReportingTo] = useState('');
  const [paymentFrequency, setPaymentFrequency] = useState<string>('biweekly');

  // Initialize expiration to 7 days from now so the field is never empty on create
  const defaultExpires = (() => {
    const d = new Date();
    d.setDate(d.getDate() + 7);
    return d.toISOString().split('T')[0];
  })();
  const [initialDefaultExpiration] = useState<string>(defaultExpires);
  const [expiresAt, setExpiresAt] = useState<string>(defaultExpires);
  const [didInitExpirationFromCompany, setDidInitExpirationFromCompany] = useState<boolean>(false);
  const [didInitEmploymentTypeFromCompany, setDidInitEmploymentTypeFromCompany] =
    useState<boolean>(false);
  // Track if offer data was loaded (edit mode) - prevents company defaults from overriding saved values
  // Known from the first render when editing: hydration and the company-default
  // initializer can run in the same commit, and the initializer would land last.
  const [offerDataLoaded, setOfferDataLoaded] = useState<boolean>(editingExistingOffer);

  const [benefits, setBenefits] = useState<Benefit[]>([]);
  const [newBenefitName, setNewBenefitName] = useState<string>('');
  const [newBenefitType, setNewBenefitType] = useState<string>('');
  const [newBenefitDescription, setNewBenefitDescription] = useState<string>('');
  const [_newBenefitUrl, setNewBenefitUrl] = useState<string>('');
  const [_newBenefitPdfFile, setNewBenefitPdfFile] = useState<string>('');
  const [_newBenefitPdfFilename, setNewBenefitPdfFilename] = useState<string>('');
  const [_newBenefitPdfMimetype, setNewBenefitPdfMimetype] = useState<string>('');

  // State for equity calculator
  const [equityShares, setEquityShares] = useState<string>('');
  const [preferredPrice, setPreferredPrice] = useState<string>('');
  const [strikePrice, setStrikePrice] = useState<string>('');
  const [companyGrowth, setCompanyGrowth] = useState<number[]>([]); // Default 50% growth projection

  // Convert companyBranding structure to companyInfo structure
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo>({
    name: '',
    logo: '',
    headerImage: '',
    primaryColor: '#4f46e5',
    description: '',
  });

  // State for compensation tab - initialize with company defaults if available
  const [salaryCurrency, setSalaryCurrency] = useState<CurrencyCode>(DEFAULT_CURRENCY);
  const [equityType, setEquityType] = useState<string>('');
  const [equityVesting, setEquityVesting] = useState<string>('');
  const [yearsVesting, setYearsVesting] = useState<number>(4);
  const [cliffPeriodMonths, setCliffPeriodMonths] = useState<number>(12);
  const [showEquityOwnership, setShowEquityOwnership] = useState<boolean>(false);
  const [equityDisplayCurrency, setEquityDisplayCurrency] = useState<string>('USD');
  const [equityConversionRate, setEquityConversionRate] = useState<string>('');

  // Employment type state
  const [employmentType, setEmploymentType] = useState<'full_time' | 'part_time' | 'contractor'>(
    'full_time'
  );
  const [compensationType, setCompensationType] = useState<'salary' | 'hourly'>('salary');
  const [hourlyRate, setHourlyRate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');

  // Initialize values when they're undefined
  useEffect(() => {
    if (yearsVesting === undefined) {
      setYearsVesting(0);
    }
    if (cliffPeriodMonths === undefined) {
      setCliffPeriodMonths(0);
    }
  }, [yearsVesting, cliffPeriodMonths]);

  // Initialize form with company defaults when company branding loads (apply once when available)
  // Skip if offer data was already loaded (edit mode) to preserve saved values
  useEffect(() => {
    if (
      companyBranding?.company &&
      !didInitExpirationFromCompany &&
      !didInitEmploymentTypeFromCompany &&
      !offerDataLoaded
    ) {
      const company = companyBranding.company;

      // Set default equity type if available and current value is empty
      if (company.default_equity_type && !equityType) {
        setEquityType(company.default_equity_type);
      }

      // Set default strike price if available and current value is empty
      if (
        company.default_strike_price !== null &&
        company.default_strike_price !== undefined &&
        !strikePrice
      ) {
        setStrikePrice(company.default_strike_price.toString());
      }

      // Set default preferred price if available and current value is empty
      if (
        company.default_preferred_price !== null &&
        company.default_preferred_price !== undefined &&
        !preferredPrice
      ) {
        setPreferredPrice(company.default_preferred_price.toString());
      }

      // Set default payment frequency if available and current value is the hardcoded default
      // CRITICAL: Only update if the company default is DIFFERENT from current value
      // to prevent infinite render loops when they're the same
      if (
        company.default_payment_frequency &&
        paymentFrequency === 'biweekly' &&
        company.default_payment_frequency !== 'biweekly'
      ) {
        setPaymentFrequency(company.default_payment_frequency);
      }

      // Set default employment type if available and current value is default
      if (company.default_employment_type && employmentType === 'full_time') {
        setEmploymentType(
          company.default_employment_type as 'full_time' | 'part_time' | 'contractor'
        );
      }

      // Apply company default expiration if configured and the user hasn't changed it away from the initial default
      if (
        company.default_offer_expiration_days !== null &&
        company.default_offer_expiration_days !== undefined
      ) {
        const days = company.default_offer_expiration_days;
        const d = new Date();
        d.setDate(d.getDate() + days);
        const companyDefault = d.toISOString().split('T')[0];
        if (!expiresAt || expiresAt === initialDefaultExpiration) {
          setExpiresAt(companyDefault);
        }
      }

      // CRITICAL: Always mark initialization as complete at the END of the block
      // This prevents infinite loops when company doesn't have all defaults set
      setDidInitEmploymentTypeFromCompany(true);
      setDidInitExpirationFromCompany(true);
    }
  }, [
    companyBranding,
    didInitExpirationFromCompany,
    didInitEmploymentTypeFromCompany,
    expiresAt,
    initialDefaultExpiration,
    equityType,
    strikePrice,
    preferredPrice,
    paymentFrequency,
    employmentType,
    offerDataLoaded,
  ]);

  // New multiple bonuses state
  const [bonuses, setBonuses] = useState<Bonus[]>([]);
  const [hasAnnualBonus, setHasAnnualBonus] = useState(false);
  const [hasQuarterlyBonus, setHasQuarterlyBonus] = useState(false);
  const [hasPerformanceBonus, setHasPerformanceBonus] = useState(false);
  const [hasSignOnBonus, setHasSignOnBonus] = useState(false);
  const [hasRelocationBonus, setHasRelocationBonus] = useState(false);

  // Commission state
  const [hasCommission, setHasCommission] = useState<boolean>(false);
  const [commissionType, setCommissionType] = useState<
    'percentage' | 'tiered' | 'quota_ote' | 'draw' | 'hybrid' | ''
  >('');
  const [commissionTargetAmount, setCommissionTargetAmount] = useState<string>('');
  const [commissionBaseRate, setCommissionBaseRate] = useState<string>('');
  const [commissionQuotaAmount, setCommissionQuotaAmount] = useState<string>('');
  const [commissionPayoutFrequency, setCommissionPayoutFrequency] = useState<
    'monthly' | 'quarterly' | 'biannual' | 'annual' | ''
  >('monthly');
  const [commissionHasAccelerators, setCommissionHasAccelerators] = useState<boolean>(false);
  const [commissionShowPerformanceScenarios, setCommissionShowPerformanceScenarios] =
    useState<boolean>(true);
  const [commissionDetails, setCommissionDetails] = useState<string>('');
  const [commissionTiers, setCommissionTiers] = useState<
    Array<{
      id: number;
      threshold: string;
      rate: string;
      is_incremental: boolean;
    }>
  >([]);

  // Function to add a new benefit
  const addBenefit = () => {
    if (!newBenefitName || !newBenefitType || !newBenefitDescription) return;

    const benefit: Benefit = {
      id: String(benefits.length + 1),
      name: newBenefitName,
      type: newBenefitType,
      description: newBenefitDescription,
      // New fields are added as undefined by default
      url: undefined,
      pdf_file: undefined,
      pdf_filename: undefined,
      pdf_mimetype: undefined,
    };

    setBenefits([...benefits, benefit]);
    setNewBenefitName('');
    setNewBenefitType('');
    setNewBenefitDescription('');
    setNewBenefitUrl('');
    setNewBenefitPdfFile('');
    setNewBenefitPdfFilename('');
    setNewBenefitPdfMimetype('');
  };

  // Function to remove a benefit
  const removeBenefit = (id: string) => {
    setBenefits(benefits.filter(benefit => benefit.id !== id));
  };

  // Function to calculate equity value
  const calculateEquityValue = (): EquityValues => {
    const shares = parseInt(equityShares) || 0;
    const strike = parseFloat(strikePrice) || 0;
    const preferred = parseFloat(preferredPrice) || 0;

    // Current value calculation
    const currentValue = shares * (preferred - strike);

    // Project future value based on growth rate
    const growthMultiplier = 1 + companyGrowth[0] / 100;
    const projectedValue = shares * (preferred * growthMultiplier - strike);

    return {
      currentValue: Math.max(0, currentValue),
      projectedValue: Math.max(0, projectedValue),
      year1: (currentValue * 0.25).toFixed(2),
      year2: (currentValue * 0.5).toFixed(2),
      year3: (currentValue * 0.75).toFixed(2),
      year4: currentValue.toFixed(2),
      projectedYear1: (projectedValue * 0.25).toFixed(2),
      projectedYear2: (projectedValue * 0.5).toFixed(2),
      projectedYear3: (projectedValue * 0.75).toFixed(2),
      projectedYear4: projectedValue.toFixed(2),
    };
  };

  // Function to add a bonus
  const addBonus = (
    type: 'recurring' | 'one_time',
    amount: string,
    amountType: 'flat' | 'percentage',
    details: string,
    name: string = '',
    currency?: string,
    frequency?: 'monthly' | 'quarterly' | 'biannual' | 'annual'
  ) => {
    // Check if a bonus of this type and frequency already exists
    const existingIndex = bonuses.findIndex(
      b => b.type === type && (type === 'one_time' || b.frequency === frequency)
    );

    if (existingIndex >= 0) {
      // Update existing bonus
      const updatedBonuses = [...bonuses];
      updatedBonuses[existingIndex] = {
        ...updatedBonuses[existingIndex],
        name,
        amount,
        amountType,
        details,
        ...(currency && { currency }),
        ...(frequency && { frequency }),
      };
      setBonuses(updatedBonuses);
    } else {
      // Add new bonus
      const newBonus: Bonus = {
        id: Date.now(), // Use timestamp as ID for now
        type,
        name,
        amount,
        amountType,
        details,
        ...(currency && { currency }),
        ...(frequency && { frequency }),
      };
      setBonuses(prevBonuses => [...prevBonuses, newBonus]);
    }
  };

  // Function to remove a bonus
  const removeBonus = (id: number) => {
    setBonuses(prevBonuses => prevBonuses.filter(bonus => bonus.id !== id));
  };

  // Function to get offer data in the correct format
  const getOfferData = (): OfferSubmitData => {
    // CLEARABLE SCALARS. The backend reads a key's PRESENCE as the instruction,
    // so `field || undefined` drops the key and a recruiter who blanks the
    // field is told nothing while the stored value survives — the field simply
    // cannot be cleared. Every scalar below is therefore sent unconditionally,
    // with the value that clears it: '' for text columns (CharField
    // allow_blank), null for the nullable ones (DRF's DateField and
    // DecimalField REJECT '' — null is how those two clear, per the write
    // serializer's own note on converting an offer back to salaried).
    //
    // `offerScalarPayload` is asserted key-by-key against an all-blank form in
    // OfferContext.cy.tsx. That test fails the moment a new `|| undefined`
    // appears here, which is the point: this was a class of bug, not four.
    const hourly = compensationType === 'hourly' ? parseFloat(hourlyRate) : NaN;
    const clearableScalars = {
      reporting_to: reportingTo,
      welcome_message: welcomeMessage,
      hourly_rate: Number.isFinite(hourly) ? hourly : null,
      end_date: endDate || null,
    };
    const data: Partial<OfferSubmitData> = {
      ...clearableScalars,
      candidate: {
        first_name: candidateName.split(' ')[0] || '',
        last_name: candidateName.split(' ').slice(1).join(' ') || '',
        email: candidateEmail,
        // phone_number is deliberately omitted: this form never collects it, and
        // the backend now treats a supplied key as an instruction to write it.
        // Sending '' here would wipe numbers populated by the ATS sync.
      },
      role: role,
      department: department,
      team: team,
      location: location,
      start_date: startDate,
      expires_at: expiresAt,
      base_salary: compensationType === 'salary' ? parseFloat(salary) || 0 : 0,
      payment_frequency: paymentFrequency as 'weekly' | 'biweekly' | 'semi_monthly' | 'monthly',
      currency: salaryCurrency,
      employment_type: employmentType,
      compensation_type: compensationType,
      company_info: {
        name: companyInfo.name,
        logo: companyInfo.logo,
        header_image: companyInfo.headerImage,
        primary_color: companyInfo.primaryColor,
        description: companyInfo.description,
      },
      company_branding_id: companyBranding?.id,
    };

    // Always send bonuses, including an empty list. The backend replaces the
    // whole set from this key, so omitting it when the list is empty is what
    // made removing the last bonus silently do nothing.
    data.bonuses = bonuses.map(bonus => ({
      type: bonus.type,
      name: bonus.name,
      amount: parseFloat(bonus.amount) || 0,
      amount_type: bonus.amountType,
      currency: bonus.currency,
      frequency: bonus.frequency,
      description: bonus.details,
    }));

    // Commission is a tri-state. The backend reads the key's presence as the
    // instruction, so only the recruiter's explicit "no commission" answer may
    // send the null that removes a stored structure. A toggle that is on but
    // not yet configured is a mid-edit state, not a removal: omit the key and
    // the stored commission survives the save.
    if (!hasCommission) {
      data.commission = null;
    }
    if (hasCommission && commissionType) {
      data.commission = {
        type: commissionType,
        target_amount: parseFloat(commissionTargetAmount) || 0,
        payout_frequency: commissionPayoutFrequency || 'monthly',
        has_accelerators: commissionHasAccelerators,
        show_performance_scenarios: commissionShowPerformanceScenarios,
        // Same `|| undefined` shape as the clearable scalars above, and
        // deliberately LEFT: `commission` is sent as a whole row that replaces
        // the stored structure, so a dropped `details` key inside it is
        // rewritten to empty either way. Nothing to clear, nothing to fix.
        details: commissionDetails || undefined,
      };

      // Add base rate for all commission types that show split amount field
      if (
        (commissionType === 'percentage' ||
          commissionType === 'tiered' ||
          commissionType === 'quota_ote') &&
        commissionBaseRate
      ) {
        data.commission.base_rate = parseFloat(commissionBaseRate) || 0;
      }

      // Add quota amount for quota_ote commission
      if (commissionType === 'quota_ote' && commissionQuotaAmount) {
        data.commission.quota_amount = parseFloat(commissionQuotaAmount) || 0;
      }

      // Add tiers for tiered commission
      if (commissionType === 'tiered' && commissionTiers.length > 0) {
        data.commission.tiers = commissionTiers.map(tier => ({
          threshold: parseFloat(tier.threshold) || 0,
          rate: parseFloat(tier.rate) || 0,
          is_incremental: tier.is_incremental,
        }));
      }
    }

    // Equity is a tri-state, because the backend reads the key itself as the
    // instruction: object = replace the grant, null = remove it, absent =
    // leave it alone. Null is reserved for an explicit removal — answering
    // "No" to the equity question empties every grant field, so an all-empty
    // section is the recruiter's deliberate no-equity state. A grant that is
    // merely *incomplete* (say, a preferred price blanked mid-edit) is
    // incidental form state: the key is omitted so a stored grant is
    // preserved rather than manufactured into a removal.
    const equityComplete = Boolean(
      equityShares && preferredPrice && (equityType === 'rsus' || strikePrice)
    );
    const equityExplicitlyEmpty = !equityShares && !preferredPrice && !strikePrice;
    if (equityComplete) {
      data.equity_details = {
        years_vesting: yearsVesting,
        cliff_period_months: cliffPeriodMonths,
        number_of_shares: parseInt(equityShares, 10) || 0,
        preferred_price: parseFloat(preferredPrice) || 0,
        strike_price: equityType === 'rsus' ? 0 : parseFloat(strikePrice) || 0,
        equity_type: equityType === 'rsus' ? 'rsus' : 'stock_options',
        show_ownership: showEquityOwnership,
        display_currency: equityDisplayCurrency,
        usd_conversion_rate: equityConversionRate ? parseFloat(equityConversionRate) : 1.0,
      };
    } else if (equityExplicitlyEmpty) {
      data.equity_details = null;
    }

    // Always send benefits, including an empty list (see bonuses above).
    data.benefits = benefits.map(benefit => ({
      name: benefit.name,
      type: benefit.type,
      description: benefit.description,
      url: benefit.url,
      monetary_value: benefit.monetary_value,
      currency: benefit.currency,
      frequency: benefit.frequency,
      pdf_file: benefit.pdf_file,
      pdf_filename: benefit.pdf_filename,
      pdf_mimetype: benefit.pdf_mimetype,
      derived_from_benefit_template: benefit.derived_from_benefit_template,
      order: benefit.order,
    }));

    return data as OfferSubmitData;
  };

  // Function to preview offer data
  const previewOffer = (): OfferSubmitData => {
    return getOfferData();
  };

  // ---- Unsaved-edit tracking ------------------------------------------- //
  // The legal document preview is compiled from the offer's SAVED values, so a
  // form with unsaved edits shows the recruiter a letter that disagrees with
  // what they are looking at. A serialized snapshot answers that honestly
  // without wrapping all sixty setters in the provider.
  //
  // `company_info` / `company_branding_id` are excluded on purpose: the app
  // loads them, the recruiter does not type them, and a branding query landing
  // after the baseline would report the form as dirty before anyone touched it.
  const formFingerprint = (): string => {
    const { company_info: _info, company_branding_id: _branding, ...edited } = getOfferData();
    return JSON.stringify(edited);
  };
  const [savedFingerprint, setSavedFingerprint] = useState<string | null>(null);
  const markOfferSaved = () => setSavedFingerprint(formFingerprint());
  // Null baseline = nothing has been saved yet (create mode), which is not the
  // same as "dirty" — there is no saved preview for it to disagree with.
  const isFormDirty = savedFingerprint !== null && savedFingerprint !== formFingerprint();

  // Function to submit offer using React Query
  const submitOffer = async (offerData: Partial<OfferSubmitData> = {}): Promise<Offer> => {
    // Merge the passed offerData with the context state
    const mergedData = {
      ...getOfferData(),
      ...offerData,
      // Ensure benefits from offerData take precedence if provided
      benefits:
        offerData.benefits ||
        benefits.map(benefit => ({
          name: benefit.name,
          type: benefit.type,
          description: benefit.description,
          url: benefit.url,
          monetary_value: benefit.monetary_value,
          currency: benefit.currency,
          frequency: benefit.frequency,
          pdf_file: benefit.pdf_file,
          pdf_filename: benefit.pdf_filename,
          pdf_mimetype: benefit.pdf_mimetype,
          derived_from_benefit_template: benefit.derived_from_benefit_template,
          order: benefit.order,
        })),
    };

    return createOfferMutation(mergedData);
  };

  // Update local company info when companyBranding changes
  useEffect(() => {
    if (companyBranding) {
      setCompanyInfo({
        name: companyBranding.company?.name || companyInfo.name,
        logo: companyBranding.logo || companyInfo.logo,
        headerImage: companyBranding.header_image || companyInfo.headerImage,
        primaryColor: companyBranding.primary_color || companyInfo.primaryColor,
        description: companyBranding.company?.description || companyInfo.description,
      });
    }
  }, [companyBranding]);

  // Use React Query hook for offer submission
  const {
    mutateAsync: createOfferMutation,
    isPending: isSubmitting,
    error: submitError,
    data: submitResult,
  } = useSubmitOffer();

  // Add a commission tier
  const addCommissionTier = (threshold: string, rate: string, is_incremental: boolean = true) => {
    const newTier = {
      id: Date.now(),
      threshold,
      rate,
      is_incremental,
    };
    setCommissionTiers(prev => [...prev, newTier]);
  };

  // Remove a commission tier
  const removeCommissionTier = (id: number) => {
    setCommissionTiers(prev => prev.filter(tier => tier.id !== id));
  };

  // Calculate OTE (On-Target Earnings)
  const calculateOTE = (): number => {
    const baseSalaryValue = parseFloat(salary) || 0;
    const commissionValue = hasCommission ? parseFloat(commissionTargetAmount) || 0 : 0;
    return baseSalaryValue + commissionValue;
  };

  return (
    <OfferContext.Provider
      value={{
        candidateName,
        setCandidateName,
        candidateEmail,
        setCandidateEmail,
        role,
        setRole,
        department,
        setDepartment,
        team,
        setTeam,
        location,
        setLocation,
        salary,
        setSalary,
        startDate,
        setStartDate,
        welcomeMessage,
        setWelcomeMessage,
        reportingTo,
        setReportingTo,
        expiresAt,
        setExpiresAt,
        paymentFrequency,
        setPaymentFrequency,
        benefits,
        setBenefits,
        newBenefitName,
        setNewBenefitName,
        newBenefitType,
        setNewBenefitType,
        newBenefitDescription,
        setNewBenefitDescription,
        addBenefit,
        removeBenefit,
        equityShares,
        setEquityShares,
        preferredPrice,
        setPreferredPrice,
        strikePrice,
        setStrikePrice,
        companyGrowth,
        setCompanyGrowth,
        calculateEquityValue,
        hasCommission,
        setHasCommission,
        commissionType,
        setCommissionType,
        commissionTargetAmount,
        setCommissionTargetAmount,
        commissionBaseRate,
        setCommissionBaseRate,
        commissionQuotaAmount,
        setCommissionQuotaAmount,
        commissionPayoutFrequency,
        setCommissionPayoutFrequency,
        commissionHasAccelerators,
        setCommissionHasAccelerators,
        commissionShowPerformanceScenarios,
        setCommissionShowPerformanceScenarios,
        commissionDetails,
        setCommissionDetails,
        commissionTiers,
        setCommissionTiers,
        addCommissionTier,
        removeCommissionTier,
        calculateOTE,
        companyInfo,
        setCompanyInfo,
        isSubmitting,
        submitOfferError: submitError || null,
        submitOfferResult: submitResult || null,
        submitOffer,
        salaryCurrency,
        setSalaryCurrency,
        equityType,
        setEquityType,
        equityVesting,
        setEquityVesting,
        bonuses,
        setBonuses,
        addBonus,
        removeBonus,
        hasAnnualBonus,
        setHasAnnualBonus,
        hasQuarterlyBonus,
        setHasQuarterlyBonus,
        hasPerformanceBonus,
        setHasPerformanceBonus,
        hasSignOnBonus,
        setHasSignOnBonus,
        hasRelocationBonus,
        setHasRelocationBonus,
        candidateInfo: {
          firstName: candidateName.split(' ')[0],
          lastName: candidateName.split(' ').slice(1).join(' '),
          position: role,
        },
        setCandidateInfo: (info: CandidateInfo) => {
          setCandidateName(`${info.firstName} ${info.lastName}`);
          setRole(info.position);
        },
        previewOffer,
        isFormDirty,
        markOfferSaved,
        yearsVesting,
        setYearsVesting,
        cliffPeriodMonths,
        setCliffPeriodMonths,
        showEquityOwnership,
        setShowEquityOwnership,
        equityDisplayCurrency,
        setEquityDisplayCurrency,
        equityConversionRate,
        setEquityConversionRate,
        employmentType,
        setEmploymentType,
        compensationType,
        setCompensationType,
        hourlyRate,
        setHourlyRate,
        endDate,
        setEndDate,
        offerDataLoaded,
        setOfferDataLoaded,
      }}
    >
      {children}
    </OfferContext.Provider>
  );
};

export const useOffer = (): OfferContextType => {
  const context = useContext(OfferContext);
  if (context === undefined) {
    throw new Error('useOffer must be used within an OfferProvider');
  }
  return context;
};
